import type { GameMeta } from '../lib/protocol';

/**
 * The feed: five wasm minigames followed by eleven web cards (sandboxed HTML pages).
 *
 * The wasm games stay first so the Phase 1 regression contract for slots 0-4 is unchanged and
 * the web cards are purely additive. The order carries no product meaning -- a real feed is
 * ranked, and interleaving classes so the next swipe is a different KIND of thing is a job for
 * the ranking layer, not a hardcoded array.
 *
 * Each wasm game was chosen to stress a different axis of the Mechanical Interest Graph. Served by GET /api/games/feed (dev middleware / static JSON).
 * Verbs are the MIG vocabulary we expect the telemetry to recover in Phase 2.
 */
export const MOCK_GAMES: GameMeta[] = [
  {
    id: 'flick-aim',
    title: 'Flick Aim',
    developer: 'Blitz Labs',
    wasm_url: '/games/flick-aim/flick-aim.wasm',
    data_url: null,
    thumbnail_url: '/games/flick-aim/thumb.svg',
    verbs: ['flick-aiming', 'twitch', 'precision'],
    accent: '#5eead4',
    description: 'Tap to aim, flick to shoot. Targets shrink — miss three and the run resets. High APM, short decision latency.',
  },
  {
    id: 'idle-clicker',
    title: 'Reactor Idle',
    developer: 'Blitz Labs',
    wasm_url: '/games/idle-clicker/idle-clicker.wasm',
    data_url: null,
    thumbnail_url: '/games/idle-clicker/thumb.svg',
    verbs: ['tapping', 'incremental', 'low-friction'],
    accent: '#facc15',
    description: 'Tap to feed the reactor, buy generators, keep the core from going cold. Passive engagement with a soft fail state.',
  },
  {
    id: 'puzzle-loop',
    title: 'Lights Loop',
    developer: 'Blitz Labs',
    wasm_url: '/games/puzzle-loop/puzzle-loop.wasm',
    data_url: null,
    thumbnail_url: '/games/puzzle-loop/thumb.svg',
    verbs: ['turn-based-logic', 'planning', 'deliberate'],
    accent: '#a78bfa',
    description: 'Toggle a cell and its neighbours; clear the board under par. Long decision latency, very low APM.',
  },
  {
    id: 'rhythm-tap',
    title: 'Pulse',
    developer: 'Blitz Labs',
    wasm_url: '/games/rhythm-tap/rhythm-tap.wasm',
    data_url: null,
    thumbnail_url: '/games/rhythm-tap/thumb.svg',
    verbs: ['rhythm', 'timing', 'precision'],
    accent: '#fb7185',
    description: 'Tap the lane when the pulse crosses the line. Timing offsets become decision-latency samples.',
  },
  {
    id: 'resource-mgmt',
    title: 'Outpost',
    developer: 'Blitz Labs',
    wasm_url: '/games/resource-mgmt/resource-mgmt.wasm',
    data_url: null,
    thumbnail_url: '/games/resource-mgmt/thumb.svg',
    verbs: ['resource-optimization', 'allocation', 'ui-dense'],
    accent: '#60a5fa',
    description: 'Assign colonists between food, ore and research every turn. Dense UI, steady decision cadence.',
  },
  {
    id: 'ripple-tap',
    kind: 'web',
    title: 'Ripple Tap',
    developer: 'Blitz Labs',
    wasm_url: '',
    page_url: '/cards/ripple-tap/index.html',
    data_url: null,
    thumbnail_url: '/cards/ripple-tap/thumb.svg',
    verbs: ['tapping', 'reactive', 'toy'],
    accent: '#a78bfa',
    description: 'Tap the field and a shockwave refracts the grid. Overlapping waves interfere. Drag to tilt the plane.',
  },
  {
    id: 'orbit-cut',
    kind: 'web',
    title: 'Orbit Cut',
    developer: 'Blitz Labs',
    wasm_url: '',
    page_url: '/cards/orbit-cut/index.html',
    data_url: null,
    thumbnail_url: '/cards/orbit-cut/thumb.svg',
    verbs: ['timing', 'precision', 'twitch'],
    accent: '#fbbf24',
    description: 'Dots orbit a core. Tap when one crosses the gate. Ten-second rounds, instantly restartable.',
  },
  {
    id: 'colour-split',
    kind: 'web',
    title: 'Colour Split',
    developer: 'Blitz Labs',
    wasm_url: '',
    page_url: '/cards/colour-split/index.html',
    data_url: null,
    thumbnail_url: '/cards/colour-split/thumb.svg',
    verbs: ['preference', 'identity', 'low-effort'],
    accent: '#f472b6',
    description: 'Two colours, one thumb. Drag to say which you prefer, six times, and get your palette back.',
  },
  {
    id: 'sand-fall',
    kind: 'web',
    title: 'Sand Fall',
    developer: 'Blitz Labs',
    wasm_url: '',
    page_url: '/cards/sand-fall/index.html',
    data_url: null,
    thumbnail_url: '/cards/sand-fall/thumb.svg',
    verbs: ['simulation', 'toy', 'tactile'],
    accent: '#f59e0b',
    description: 'Pour sand, water and stone into a falling-sand world. Sand piles, water pools and drains.',
  },
  {
    id: 'echo-lines',
    kind: 'web',
    title: 'Echo Lines',
    developer: 'Blitz Labs',
    wasm_url: '',
    page_url: '/cards/echo-lines/index.html',
    data_url: null,
    thumbnail_url: '/cards/echo-lines/thumb.svg',
    verbs: ['generative', 'ambient', 'tactile'],
    accent: '#38bdf8',
    description: 'Flowing contour lines bend around your finger and slowly relax back. No goal, no score.',
  },
  {
    id: 'stack-drop',
    kind: 'web',
    title: 'Stack Drop',
    developer: 'Blitz Labs',
    wasm_url: '',
    page_url: '/cards/stack-drop/index.html',
    data_url: null,
    thumbnail_url: '/cards/stack-drop/thumb.svg',
    verbs: ['timing', 'stacking', 'arcade'],
    accent: '#34d399',
    description: 'A block swings above the tower. Tap to drop it. Overhang is sliced off and the tower narrows.',
  },
  {
    id: 'pulse-lock',
    kind: 'web',
    title: 'Pulse Lock',
    developer: 'Blitz Labs',
    wasm_url: '',
    page_url: '/cards/pulse-lock/index.html',
    data_url: null,
    thumbnail_url: '/cards/pulse-lock/thumb.svg',
    verbs: ['timing', 'precision', 'drill'],
    accent: '#22d3ee',
    description: 'An expanding ring meets a fixed target. Tap on the beat. Perfect hits chain a multiplier.',
  },
  {
    id: 'tide-pool',
    kind: 'web',
    title: 'Tide Pool',
    developer: 'Blitz Labs',
    wasm_url: '',
    page_url: '/cards/tide-pool/index.html',
    data_url: null,
    thumbnail_url: '/cards/tide-pool/thumb.svg',
    verbs: ['ambient', 'creatures', 'calm'],
    accent: '#2dd4bf',
    description: 'Procedural creatures school and scatter around your finger. Tap to feed them. Nothing to lose.',
  },
  {
    id: 'tile-flip',
    kind: 'web',
    title: 'Tile Flip',
    developer: 'Blitz Labs',
    wasm_url: '',
    page_url: '/cards/tile-flip/index.html',
    data_url: null,
    thumbnail_url: '/cards/tile-flip/thumb.svg',
    verbs: ['logic', 'turn-based', 'puzzle'],
    accent: '#c084fc',
    description: 'Flip a tile and its neighbours flip too. Make the grid one colour. No timer, high decision latency.',
  },
  {
    id: 'gravity-well',
    kind: 'web',
    title: 'Gravity Well',
    developer: 'Blitz Labs',
    wasm_url: '',
    page_url: '/cards/gravity-well/index.html',
    data_url: null,
    thumbnail_url: '/cards/gravity-well/thumb.svg',
    verbs: ['physics', 'toy', 'spectacle'],
    accent: '#818cf8',
    description: 'Tap to drop a gravity well into a particle stream and bend it into orbits. Wells decay.',
  },
  {
    id: 'bloom',
    kind: 'web',
    title: 'Bloom',
    developer: 'Blitz Labs',
    wasm_url: '',
    page_url: '/cards/bloom/index.html',
    data_url: null,
    thumbnail_url: '/cards/bloom/thumb.svg',
    verbs: ['generative', 'ambient', 'tactile'],
    accent: '#4ade80',
    description: 'Tap to plant. Procedural growth, blossoms at the tips, horizontal drag for wind.',
  },
  {
    // The first card that is nothing but a URL: a site on its own origin, framed as-is. It is
    // here (rather than only in the database) so the linked-site path is exercised by every
    // build and visible in production before the submit flow exists.
    id: 'deepstate-demo',
    kind: 'web',
    source: 'external',
    title: 'Deepstate',
    developer: 'deepstate-demo.netlify.app',
    wasm_url: '',
    page_url: 'https://deepstate-demo.netlify.app/',
    data_url: null,
    thumbnail_url: '/cards/linked/thumb.svg',
    verbs: ['linked', 'site'],
    accent: '#38bdf8',
    description: 'A linked website. Same feed, same BLITZ button — the page is just a URL.',
  },
];
